/* Showing the reader where their search words are: in the list's sender and
 * subject, and in the open message's text.
 *
 * **Matching is folded like the search itself**, so "cafe" marks "café" and
 * "καλημερα" marks "Καλημέρα". Folding changes lengths (an accented letter
 * decomposes), so the text is folded one character at a time with a map from
 * each folded position back to the original, and the marks go on the
 * original text.
 */

import { fold } from "../engine/text.js";

/** Folded text plus, for every folded position, where it came from in the original. */
export function foldWithMap(text) {
  let folded = "";
  const map = [];
  for (let i = 0; i < text.length;) {
    const char = String.fromCodePoint(text.codePointAt(i));
    const piece = fold(char);
    for (let k = 0; k < piece.length; k++) {
      folded += piece[k];
      map.push(i);
    }
    i += char.length;
  }
  map.push(text.length);
  return { folded, map };
}

/**
 * Where each term occurs in `text`, as [start, end) ranges in the ORIGINAL
 * text, sorted and merged. Terms are expected folded already (the query is).
 */
export function matchRanges(text, terms) {
  if (!text || !terms.length) return [];
  const { folded, map } = foldWithMap(text);
  const ranges = [];

  for (const term of terms) {
    if (!term) continue;
    for (let at = folded.indexOf(term); at >= 0; at = folded.indexOf(term, at + term.length)) {
      ranges.push([map[at], map[at + term.length]]);
    }
  }

  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([...range]);
  }
  return merged;
}

/** Sets `element`'s text, with each match wrapped in <mark>. */
export function setMarkedText(element, text, terms) {
  const ranges = matchRanges(text, terms);
  if (!ranges.length) {
    element.textContent = text;
    return 0;
  }

  const document = element.ownerDocument;
  const parts = [];
  let at = 0;
  for (const [start, end] of ranges) {
    if (start > at) parts.push(document.createTextNode(text.slice(at, start)));
    const mark = document.createElement("mark");
    mark.dataset.mbMark = "";
    mark.textContent = text.slice(start, end);
    parts.push(mark);
    at = end;
  }
  if (at < text.length) parts.push(document.createTextNode(text.slice(at)));
  element.replaceChildren(...parts);
  return ranges.length;
}

/**
 * Removes every mark THIS FILE made inside `root` (an email's own <mark> is
 * left alone), leaving the text exactly
 * as it was: each mark becomes its text again, and the pieces are joined.
 */
export function clearMarks(root) {
  const parents = new Set();
  for (const mark of root.querySelectorAll("mark[data-mb-mark]")) {
    parents.add(mark.parentNode);
    mark.replaceWith(...mark.childNodes);
  }
  for (const parent of parents) parent.normalize();
}

/**
 * Marks every match inside a rendered message body (a shadow root or any
 * element). Walks text nodes only, and never inside style or script, so the
 * email's markup is untouched apart from the marks. Returns how many.
 */
export function markMatches(root, terms) {
  if (!terms.length) return 0;
  const document = root.ownerDocument ?? root.host?.ownerDocument;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => node.parentElement?.closest("style, script, textarea, mark")
      ? NodeFilter.FILTER_REJECT
      : NodeFilter.FILTER_ACCEPT,
  });

  const nodes = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node);

  let marked = 0;
  for (const node of nodes) {
    const ranges = matchRanges(node.nodeValue, terms);
    if (!ranges.length) continue;
    // Right to left, so earlier offsets stay true while later ones are split off.
    for (let i = ranges.length - 1; i >= 0; i--) {
      const [start, end] = ranges[i];
      const middle = node.splitText(start);
      middle.splitText(end - start);
      const mark = document.createElement("mark");
      mark.dataset.mbMark = ""; // ours, so clearing never touches an email's own <mark>
      mark.appendChild(middle.cloneNode());
      middle.replaceWith(mark);
      marked++;
    }
  }
  return marked;
}
